import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { appelApi } from './api';
import type { ChoixPeriode } from './useDashboardDirection';
import type { IndicateurSynthese } from './useSynthesesMensuelles';

/**
 * « Analytics & ROI » de la direction (TF-479) : les indicateurs arrivent prêts
 * à afficher (`domain/analytics.ts`, présentés comme ceux de la synthèse
 * mensuelle) ; l'écran ne recompte rien.
 */

export interface Heures {
  minutes: number;
  texte: string;
}

export type Modalite = 'minutes' | 'presentiel' | 'distance';

export interface PointMensuel extends Record<Modalite, number> {
  mois: string;
  court: string;
  libelle: string;
  /** Chaque durée lisible : « 1 234 h ». */
  textes: Record<Modalite, string>;
}

export interface CompletionFormation {
  code: string;
  titre: string;
  taux: number;
  texte: string;
  apprenants: number;
  sansTaux: number;
}

export interface VolumeFormation {
  code: string;
  titre: string;
  apprenants: number;
  minutes: number;
  heures: string;
  detail: string;
}

export interface DonneesAnalytics {
  calculeLe: string;
  periode: { choix: ChoixPeriode; du: string; au: string; arreteeAu: string; libelle: string };
  comparaison: { du: string; au: string; libelle: string };
  filtres: { periodes: { valeur: ChoixPeriode; libelle: string }[] };
  /** Heures formées, taux de complétion, satisfaction, chiffre d'affaires. */
  indicateurs: IndicateurSynthese[];
  heures: { serie: PointMensuel[]; libelle: string; total: Heures; presentiel: Heures; distance: Heures };
  completion: {
    taux: number | null;
    apprenants: number;
    sansTaux: number;
    formations: CompletionFormation[];
    autres: number;
    sansCours: number;
  };
  impact: IndicateurSynthese[];
  formations: VolumeFormation[];
}

export interface FiltreAnalytics {
  site: string;
  periode: ChoixPeriode;
  du: string;
  au: string;
}

/** La période et l'antenne vont à l'API ; une période personnalisée incomplète n'est pas demandée. */
export function useAnalytics(filtre: FiltreAnalytics) {
  const parametres = new URLSearchParams();
  if (filtre.site && filtre.site !== 'TOUS') parametres.set('site', filtre.site);
  parametres.set('periode', filtre.periode);
  if (filtre.periode === 'LIBRE') {
    parametres.set('du', filtre.du);
    parametres.set('au', filtre.au);
  }
  const chaine = parametres.toString();
  const libreIncomplete = filtre.periode === 'LIBRE' && (!filtre.du || !filtre.au || filtre.du > filtre.au);

  return useQuery({
    // Sous « financements » : une facture émise ou un règlement saisi l'actualisent aussi.
    queryKey: ['financements', 'analytics', chaine],
    queryFn: () => appelApi<DonneesAnalytics>(`/analytics?${chaine}`),
    enabled: !libreIncomplete,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
  });
}
